import { Modules } from '@enum/central/modules.enum';
import { PlanCapabilitiesService, planoIncluiModulo } from '@shared/services/plan-capabilities.service';
import { OnboardingService } from '@core/onboarding/onboarding.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ComponentRef, Injectable, NgZone, Signal, computed, effect, inject, signal, untracked } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, finalize, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TenancyService } from '@core/services/common';
import { AuthService } from '@core/services/common/auth.service';
import { NotificationService } from '@core/services/notification.service';
import { NotificationType } from '@enum/common/notification-type.enum';
import { JornadaRoute } from '@core/services/api/routes/tenancy/jornada.route';
import { ATRASO_DE_DISPARO_MS, TutorialService } from '@core/tutorial/tutorial.service';
import { JornadaOverlayComponent } from './jornada-overlay.component';
import { JornadaResposta, MissaoResposta } from './jornada.model';
import { deveAbrir, novidades } from './regra-de-abertura';

/** Sufixo da chave — o prefixo é `${tenant}.${usuario}.`, a convenção de `dashboard-filter.service.ts`. */
export const CHAVE_ULTIMA_ABERTURA_SUFIXO = 'jornada.ultima-abertura';

/** A API responde 403 quando falta a permissão `_Update` do domínio da missão; o interceptor global é mudo para 403. */
export const AVISO_SEM_PERMISSAO = 'Só quem administra essa área pode marcar não se aplica.';
export const AVISO_FALHA_AO_SALVAR = 'Não foi possível salvar a alteração da missão. Tente de novo em instantes.';

/**
 * A Órbita Financeira — estado, abertura automática e o overlay de tela cheia.
 *
 * `root`, construído pela barra superior (como o `TutorialService`). Abre
 * sozinho quando (a) o tour do painel termina e (b) a regra pura `deveAbrir`
 * diz sim; reabre pelo botão do cabeçalho. A "última abertura" fica em
 * `localStorage` por tenant+usuário — se um dia tiver de valer entre
 * dispositivos, é endpoint novo (spec §7).
 *
 * Sem `takeUntilDestroyed`: o serviço é `root` e morre com a aplicação — a
 * inscrição é a própria vida dele (mesma decisão do tutorial).
 */
@Injectable({ providedIn: 'root' })
export class JornadaService {
  private readonly onboarding = inject(OnboardingService);
  private readonly plano = inject(PlanCapabilitiesService);
  private readonly overlay = inject(Overlay);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly tenancy = inject(TenancyService);
  private readonly auth = inject(AuthService);
  private readonly api = inject(JornadaRoute);
  private readonly tutorial = inject(TutorialService);
  private readonly notificacao = inject(NotificationService);

  private readonly dadosSignal = signal<JornadaResposta | null>(null);
  private readonly abertaSignal = signal(false);
  private readonly carregandoSignal = signal(false);
  private readonly ultimaAberturaSignal = signal<string | null>(null);

  readonly dados: Signal<JornadaResposta | null> = this.dadosSignal.asReadonly();
  readonly aberta: Signal<boolean> = this.abertaSignal.asReadonly();
  readonly carregando: Signal<boolean> = this.carregandoSignal.asReadonly();
  readonly idsNovos: Signal<string[]> = computed(() => {
    const dados = this.dadosSignal();

    return dados ? novidades(dados, this.ultimaAberturaSignal()) : [];
  });

  /**
   * CV-CLI-31 — a missão cuja escrita de exceção está em voo. Quem dispara a
   * chamada é este serviço (o overlay só EMITE a intenção), então é daqui que
   * o botão da lista recebe o que precisa para girar em vez de ficar mudo.
   */
  private readonly excecaoEmAndamentoSignal = signal<string | null>(null);
  readonly excecaoEmAndamento: Signal<string | null> = this.excecaoEmAndamentoSignal.asReadonly();

  private geracao = 0;
  private overlayRef?: OverlayRef;
  private jaAbriuNestaSessao = false;
  private aguardandoRetornoDeMissao = false;
  private disparoAgendado?: ReturnType<typeof setTimeout>;

  constructor() {
    this.ultimaAberturaSignal.set(this.lerUltimaAbertura());

    if (!this.tenancy.isSubdomain) {
      return; // no admin não há jornada
    }

    let contextoAnterior = this.onboarding.contexto();
    if (this.orbitaLiberada()) this.recarregar();
    effect(() => {
      const contexto = this.onboarding.contexto();
      const liberada = this.orbitaLiberada();
      untracked(() => {
      if (contexto !== contextoAnterior) {
        contextoAnterior = contexto;
        ++this.geracao;
        this.overlayRef?.dispose();
        this.overlayRef = undefined;
        this.abertaSignal.set(false);
        this.dadosSignal.set(null);
        this.carregandoSignal.set(false);
        this.aguardandoRetornoDeMissao = false;
        this.jaAbriuNestaSessao = false;
        this.ultimaAberturaSignal.set(this.lerUltimaAbertura());
      }
      if (liberada) {
        if (!this.dadosSignal() && !this.carregandoSignal()) this.recarregar();
        this.agendarTentativa();
      } else {
        ++this.geracao;
        clearTimeout(this.disparoAgendado);
        this.overlayRef?.dispose();
        this.overlayRef = undefined;
        this.abertaSignal.set(false);
        this.dadosSignal.set(null);
        this.carregandoSignal.set(false);
        this.jaAbriuNestaSessao = false;
      }
      });
    });
    // O tour que acabou de encerrar É o tour visto: a emissão vem depois de `marcarComoVisto`.
    this.tutorial.encerrado$.pipe(filter(id => id === 'dashboard')).subscribe(() => this.tentarAbrir(true));
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(evento => {
      const caminho = evento.urlAfterRedirects.split(/[?#]/)[0].replace(/\/+$/, '');
      if (this.aguardandoRetornoDeMissao && caminho === '/painel/dashboard') {
        this.aguardandoRetornoDeMissao = false;
        this.recarregar();
      }
      this.agendarTentativa();
    });
    this.agendarTentativa();
  }

  /**
   * Falha do GET é silenciosa AQUI de propósito: a chamada nasce com a barra
   * superior em toda tela do tenant (o `carregar()` leva `IGNORAR_ERRO_GLOBAL`,
   * então o interceptor também não avisa). `dados` fica como está — nulo se
   * nunca chegou —, `carregando` cai, a abertura automática não acontece
   * (`deveAbrir` exige dado) e o overlay, se aberto, diz que não conseguiu
   * calcular. O botão do cabeçalho continua funcionando.
   */
  private orbitaLiberada(): boolean {
    return this.onboarding.orbitaLiberada() && planoIncluiModulo(this.plano.modulos, Modules.FINANCIAL_ORBIT);
  }

  recarregar(): void {
    if (!this.orbitaLiberada()) return;
    const geracao = ++this.geracao;
    const contexto = this.onboarding.contexto();
    this.carregandoSignal.set(true);
    this.api.carregar().subscribe({
      next: dados => {
        if (geracao !== this.geracao || contexto !== this.onboarding.contexto() || !this.orbitaLiberada()) return;
        // Resposta fora do contrato (sem `fases`) vale como "sem dado": o
        // `idsNovos` percorre `fases`, e o cabeçalho o lê em toda tela.
        this.dadosSignal.set(Array.isArray(dados?.fases) ? dados : null);
        this.carregandoSignal.set(false);
      },
      error: () => { if (geracao === this.geracao) this.carregandoSignal.set(false); },
    });
  }

  abrir(): void {
    if (!this.orbitaLiberada() || this.abertaSignal()) {
      return;
    }
    // Mostra o retrato anterior enquanto consulta o progresso feito nas telas.
    // Se a consulta inicial ainda está em voo, ela atende esta abertura também.
    if (!this.carregandoSignal()) {
      this.recarregar();
    }
    this.exibirOverlay();
  }

  fechar(): void {
    if (!this.abertaSignal()) {
      return;
    }
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.abertaSignal.set(false);
    this.gravarUltimaAbertura();
  }

  naoSeAplica(id: string, justificativa: string): Observable<MissaoResposta> {
    return this.api.naoSeAplica(id, justificativa).pipe(tap(() => this.recarregar()));
  }

  removerExcecao(id: string): Observable<void> {
    return this.api.removerExcecao(id).pipe(tap(() => this.recarregar()));
  }

  private tentarAbrir(tourDoPainelVisto = this.tutorial.jaViu('dashboard')): void {
    if (!this.orbitaLiberada() || !this.onboarding.estado()?.celebrado) return;
    const abre = deveAbrir({
      rota: this.router.url,
      tourDoPainelVisto,
      jaAbriuNestaSessao: this.jaAbriuNestaSessao,
      ultimaAbertura: this.ultimaAberturaSignal(),
      jornada: this.dadosSignal(),
    });

    if (abre) {
      // A abertura automática já depende do retrato carregado pelo construtor.
      this.exibirOverlay();
    }
  }

  private exibirOverlay(): void {
    if (!this.orbitaLiberada()) return;
    this.jaAbriuNestaSessao = true;
    this.montarOverlay();
    this.abertaSignal.set(true);
  }

  /** Mesmo atraso do tutorial, fora da zone: o painel ainda está chegando por HTTP. */
  private agendarTentativa(): void {
    clearTimeout(this.disparoAgendado);
    if (!this.orbitaLiberada()) return;
    this.zone.runOutsideAngular(() => {
      this.disparoAgendado = setTimeout(() => this.zone.run(() => this.tentarAbrir()), ATRASO_DE_DISPARO_MS);
    });
  }

  /**
   * Os `setInput` passam os SIGNALS (a instância, não o valor): o overlay lê
   * `dados()` no template, e uma recarga depois de "não se aplica" atualiza a
   * tela sem remontar.
   */
  private montarOverlay(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: false, // o próprio componente pinta o fundo petróleo
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay.position().global(),
      panelClass: 'mg-jornada__painel-overlay',
    });
    const ref: ComponentRef<JornadaOverlayComponent> = this.overlayRef.attach(new ComponentPortal(JornadaOverlayComponent));
    ref.setInput('dados', this.dadosSignal);
    ref.setInput('carregando', this.carregandoSignal);
    ref.setInput('idsNovos', this.idsNovos);
    ref.setInput('excecaoEmAndamento', this.excecaoEmAndamento);
    ref.instance.fechar.subscribe(() => this.fechar());
    ref.instance.irPara.subscribe(rota => {
      this.aguardandoRetornoDeMissao = true;
      this.fechar();
      void this.router.navigateByUrl(rota);
    });
    // As escritas avisam em caso de erro: o interceptor global é mudo para
    // 403 (quem não tem a permissão `_Update` do domínio ouviria silêncio) e a
    // lista guarda a justificativa até a recarga de sucesso trocar `missoes`.
    ref.instance.naoSeAplica.subscribe(({ id, justificativa }) => {
      this.excecaoEmAndamentoSignal.set(id);
      this.naoSeAplica(id, justificativa)
        .pipe(finalize(() => this.excecaoEmAndamentoSignal.set(null)))
        .subscribe({ error: erro => this.avisarFalhaDaEscrita(erro) });
    });
    ref.instance.removerExcecao.subscribe(id => {
      this.excecaoEmAndamentoSignal.set(id);
      this.removerExcecao(id)
        .pipe(finalize(() => this.excecaoEmAndamentoSignal.set(null)))
        .subscribe({ error: erro => this.avisarFalhaDaEscrita(erro) });
    });
    this.overlayRef.keydownEvents().subscribe(evento => {
      if (evento.key === 'Escape') {
        evento.preventDefault();
        this.fechar();
      }
    });
  }

  private avisarFalhaDaEscrita(erro: unknown): void {
    const semPermissao = erro instanceof HttpErrorResponse && erro.status === 403;
    this.notificacao.open(semPermissao ? AVISO_SEM_PERMISSAO : AVISO_FALHA_AO_SALVAR, NotificationType.DANGER);
  }

  private chave(): string {
    const tenant = this.tenancy.subdomain || 'main';
    const usuario = this.auth.currentUser?.id || 'anonimo';

    return `${tenant}.${usuario}.${CHAVE_ULTIMA_ABERTURA_SUFIXO}`;
  }

  private lerUltimaAbertura(): string | null {
    try {
      const bruto = localStorage.getItem(this.chave());

      return bruto && !Number.isNaN(Date.parse(bruto)) ? bruto : null;
    } catch {
      return null;
    }
  }

  private gravarUltimaAbertura(): void {
    const agora = new Date().toISOString();
    this.ultimaAberturaSignal.set(agora);
    try {
      localStorage.setItem(this.chave(), agora);
    } catch {
      // sem armazenamento a jornada reabre na próxima visita — degradação aceitável
    }
  }
}
